import { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, Outlet } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "@/context/CartContext";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { CartDrawer } from "@/components/site/CartDrawer";
import { useFetch } from "@/hooks/useFetch";
import Home from "@/pages/Home";
import FaqPage from "@/pages/FaqPage";
import ContentPage from "@/pages/ContentPage";
import DealerPage from "@/pages/DealerPage";
import { ToolsIndex, BrandPage } from "@/pages/Tools";
import Shop from "@/pages/Shop";
import ProductPage from "@/pages/ProductPage";
import { CartPage, CheckoutPage, CheckoutSuccess, CheckoutCancel } from "@/pages/Checkout";
import { Downloads, DownloadDetail, Blog, BlogPost, Contact } from "@/pages/Misc";
import NotFound from "@/pages/NotFound";

const AdminApp = lazy(() => import("@/admin/AdminApp"));

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => window.scrollTo(0, 0), [pathname]);
  return null;
}

/** Client-side fallback for the 301 map when the dev-server middleware is not in front. */
function RedirectOr404() {
  const { pathname } = useLocation();
  const { data } = useFetch("/redirects");
  if (!data) return null;
  const hit = data.find((r) => r.from === pathname.replace(/\/+$/, "") || r.from === pathname.toLowerCase());
  return hit ? <Navigate to={hit.to} replace /> : <NotFound />;
}

function SiteLayout() {
  return (
    <div className="flex min-h-screen flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:bg-white focus:px-4 focus:py-2 focus:text-black">Skip to content</a>
      <Header />
      <main id="main" className="flex-1"><Outlet /></main>
      <Footer />
      <CartDrawer />
    </div>
  );
}

const PAGES = [
  ["/ecu-remapping", "ecu-remapping"], ["/services", "services"], ["/about", "about"],
  ["/shipping-returns", "shipping"], ["/terms-conditions", "terms"], ["/privacy-policy", "privacy"],
  ["/tuning-files", "tuning-files"], ["/cookie-policy", "cookies"],
];

export default function App() {
  return (
    <HelmetProvider>
        <CartProvider>
          <BrowserRouter>
            <ScrollTop />
            <Routes>
              <Route path="/admin/*" element={<Suspense fallback={<div className="p-10 text-slate-400">Loading…</div>}><AdminApp /></Suspense>} />
              <Route element={<SiteLayout />}>
                <Route index element={<Home />} />
                {PAGES.map(([p, k]) => <Route key={p} path={p} element={<ContentPage pageKey={k} />} />)}
                <Route path="/remappingfranchise" element={<DealerPage />} />
                <Route path="/faq" element={<FaqPage />} />
                <Route path="/tuning-tools" element={<ToolsIndex />} />
                <Route path="/tuning-tools/:brand" element={<BrandPage />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/shop/category/:slug" element={<Shop />} />
                <Route path="/product-page/:slug" element={<ProductPage />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                <Route path="/checkout/cancel" element={<CheckoutCancel />} />
                <Route path="/software-downloads" element={<Downloads />} />
                <Route path="/software-downloads/:slug" element={<DownloadDetail />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/post/:slug" element={<BlogPost />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="*" element={<RedirectOr404 />} />
              </Route>
            </Routes>
            <Toaster theme="dark" position="bottom-right" />
          </BrowserRouter>
        </CartProvider>
    </HelmetProvider>
  );
}
